import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CreditUsagePanel = ({ 
  creditBalance, 
  creditLimit, 
  subscriptionTier,
  renewalDate,
  onUpgrade,
  isUnlimited = false
}) => {
  const navigate = useNavigate();
  const usagePercentage = isUnlimited ? 100 : (creditBalance / creditLimit) * 100;
  const isLowCredit = !isUnlimited && usagePercentage < 20;
  const isCritical = !isUnlimited && usagePercentage < 10;

  const getStatusColor = () => {
    if (isUnlimited) return 'bg-success';
    if (usagePercentage >= 50) return 'bg-success';
    if (usagePercentage >= 20) return 'bg-warning';
    return 'bg-error';
  };

  const getStatusText = () => {
    if (isUnlimited) return 'Unlimited';
    if (usagePercentage >= 50) return 'Healthy';
    if (usagePercentage >= 20) return 'Low';
    return 'Critical';
  };

  return (
    <div className="clinical-card p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Credit Usage</h3>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-md">
          <Icon name="Crown" size={16} color="var(--color-primary)" />
          <span className="text-xs font-medium text-primary">{subscriptionTier}</span>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-end justify-between mb-2">
          <div>
            {isUnlimited ? (
              <div className="flex items-center gap-2">
                <Icon name="Infinity" size={28} color="var(--color-success)" />
                <span className="text-xl font-bold text-success font-mono">Unlimited</span>
              </div>
            ) : (
              <>
                <span className="text-3xl font-bold text-foreground font-mono">{creditBalance}</span>
                <span className="text-sm text-muted-foreground ml-2">/ {creditLimit} credits</span>
              </>
            )}
          </div>
          <span className={`text-xs font-semibold px-2 py-1 rounded ${
            isUnlimited ? 'bg-success/10 text-success' :
            usagePercentage >= 50 ? 'bg-success/10 text-success' :
            usagePercentage >= 20 ? 'bg-warning/10 text-warning': 'bg-error/10 text-error'
          }`}>
            {getStatusText()}
          </span>
        </div>
        
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ${getStatusColor()}`}
            style={{ width: `${usagePercentage}%` }}
          />
        </div>
      </div>

      {(isLowCredit || isCritical) && (
        <div className={`p-3 rounded-lg mb-4 ${
          isCritical ? 'bg-error/10 border border-error/20' : 'bg-warning/10 border border-warning/20'
        }`}>
          <div className="flex items-start gap-2">
            <Icon 
              name={isCritical ? 'AlertCircle' : 'AlertTriangle'} 
              size={16} 
              color={isCritical ? 'var(--color-error)' : 'var(--color-warning)'} 
              className="mt-0.5"
            />
            <div className="flex-1">
              <p className={`text-sm font-medium ${isCritical ? 'text-error' : 'text-warning'}`}>
                {isCritical ? 'Critical Credit Level' : 'Low Credit Warning'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {isCritical 
                  ? 'You have less than 10% credits remaining. Upgrade now to continue service.' :'Consider upgrading your plan to ensure uninterrupted access.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {isUnlimited && (
        <div className="p-3 rounded-lg mb-4 bg-success/10 border border-success/20">
          <div className="flex items-start gap-2">
            <Icon name="ShieldCheck" size={16} color="var(--color-success)" className="mt-0.5" />
            <p className="text-sm text-success font-medium">Admin account — unlimited credits</p>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Renewal Date</span>
          <span className="font-medium text-foreground">{renewalDate}</span>
        </div>
        {!isUnlimited && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Usage This Month</span>
            <span className="font-medium text-foreground">{creditLimit - creditBalance} credits</span>
          </div>
        )}
      </div>

      {!isUnlimited && (
        <div className="mt-6 space-y-2">
          {usagePercentage >= 80 && (
            <Button 
              variant="primary" 
              size="sm"
              fullWidth
              iconName="ShoppingCart"
              onClick={() => navigate('/purchase-credits')}
            >
              Purchase More Credits
            </Button>
          )}
          <Button 
            variant={usagePercentage >= 80 ? "outline" : "primary"}
            size="sm"
            fullWidth
            iconName="TrendingUp"
            onClick={onUpgrade}
          >
            Upgrade Plan
          </Button>
        </div>
      )}
    </div>
  );
};

export default CreditUsagePanel;